import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { useUser } from "@/contexts/UserContext";
import { trackSubscriptionStarted } from "@/lib/analytics";
import { rdtTrack } from "@/lib/redditPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

const POLL_TIMEOUT_MS = 30000;
const MIN_VISIBLE_MS = 900;
const POLL_DELAYS_MS = [400, 800, 1200, 2000];

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isActiveSubscriptionStatus = (status) => status === "premium" || status === "trial";

/**
 * Ask the backend to settle a Stripe checkout session directly.
 *
 * Without this the page could only wait for the `checkout.session.completed`
 * webhook to land, so a slow or misconfigured webhook left a user who had
 * genuinely paid staring at "still confirming" with a Refresh button that
 * polled the same unchanged status. /subscription/checkout-status reads the
 * session from Stripe and writes the entitlement itself, which turns that
 * into a confirmation the page can actually reach on its own.
 */
const settleStripeSession = async (sessionId) => {
  if (!sessionId) return false;
  try {
    const { data } = await axios.get(
      `${API}/subscription/checkout-status/${encodeURIComponent(sessionId)}`
    );
    return data?.payment_status === "paid" || data?.payment_status === "trial";
  } catch (err) {
    // Non-fatal: the webhook is still the primary path, and polling continues.
    console.warn("[PremiumSuccess] checkout-status failed:", err?.message || err);
    return false;
  }
};

const isUserPremium = (u) => {
  if (!u) return false;
  const status = u.subscription_status || u.profile?.subscription_status;
  const role = u.role || u.profile?.role;
  return (
    isActiveSubscriptionStatus(status) ||
    role === "admin" ||
    role === "betatester"
  );
};

const PremiumSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser, isPremium } = useUser();
  const returnPath = searchParams.get("return") || "/dashboard";
  const isWeb = !(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.());

  const [stage, setStage] = useState("confirming");
  const [pollRun, setPollRun] = useState(0);
  const [continuing, setContinuing] = useState(false);

  const navigateRef = useRef(navigate);
  const returnPathRef = useRef(returnPath);
  const refreshUserRef = useRef(refreshUser);
  const userRef = useRef(user);
  const isPremiumRef = useRef(isPremium);
  const trackedRef = useRef(false);

  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { returnPathRef.current = returnPath; }, [returnPath]);
  useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { isPremiumRef.current = isPremium; }, [isPremium]);

  const getCurrentUserId = () => {
    const currentUser = userRef.current;
    if (currentUser?.user_id) return currentUser.user_id;
    if (currentUser?.id) return currentUser.id;
    try {
      return localStorage.getItem("betterhusbandUserId") || "";
    } catch (_) {
      return "";
    }
  };

  const trackConfirmedSubscription = () => {
    if (trackedRef.current) return;
    const uid = getCurrentUserId();
    if (!uid) return;
    trackedRef.current = true;
    Promise.resolve(trackSubscriptionStarted(uid, { planType: "premium" })).catch(() => {});
    // No charge has happened yet for a trial — report it as SignUp, not Purchase.
    // The authoritative Purchase event (with the real amount) is sent server-side
    // from the Stripe webhook once money actually moves. conversionId reuses the
    // Stripe session_id so Reddit dedupes this pixel fire against that server call.
    const status = userRef.current?.subscription_status || userRef.current?.profile?.subscription_status;
    const sessionId = searchParams.get("session_id");
    rdtTrack(status === "trial" ? "SignUp" : "Purchase", sessionId ? { conversionId: sessionId } : {});
  };

  useEffect(() => {
    let cancelled = false;
    const mountedAt = Date.now();

    const markConfirmed = async () => {
      try {
        if (refreshUserRef.current) {
          await refreshUserRef.current();
        }
      } catch (_) {
        // Subscription status already confirmed; context refresh can retry later.
      }

      const wait = Math.max(0, MIN_VISIBLE_MS - (Date.now() - mountedAt));
      if (wait > 0) await delay(wait);
      if (cancelled) return;

      trackConfirmedSubscription();
      setStage("confirmed");
    };

    const pollForConfirmation = async () => {
      setStage("confirming");

      if (isPremiumRef.current || isUserPremium(userRef.current)) {
        await markConfirmed();
        return;
      }

      // Web checkout returns here with ?session_id=. Settle it directly rather
      // than waiting on the webhook — this is what makes Refresh able to
      // resolve a stuck confirmation instead of re-reading the same status.
      const stripeSessionId = searchParams.get("session_id");
      if (stripeSessionId) {
        await settleStripeSession(stripeSessionId);
        if (cancelled) return;
      }

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let attempt = 0;

      while (!cancelled && Date.now() < deadline) {
        const uid = getCurrentUserId();

        try {
          if (uid) {
            const response = await axios.get(`${API}/subscription/status/${encodeURIComponent(uid)}`);
            if (isActiveSubscriptionStatus(response?.data?.subscription_status)) {
              await markConfirmed();
              return;
            }
          }

          if (refreshUserRef.current) {
            await refreshUserRef.current();
          }

          if (isPremiumRef.current || isUserPremium(userRef.current)) {
            await markConfirmed();
            return;
          }
        } catch (err) {
          console.warn("[PremiumSuccess] Subscription confirmation poll failed:", err?.message || err);
        }

        // Retry the direct settle on later attempts too: the first call can
        // land before Stripe has finished marking the session complete.
        if (stripeSessionId && attempt > 0 && attempt % 2 === 0) {
          await settleStripeSession(stripeSessionId);
          if (cancelled) return;
        }

        const wait = POLL_DELAYS_MS[Math.min(attempt, POLL_DELAYS_MS.length - 1)];
        attempt += 1;
        await delay(wait);
      }

      if (!cancelled) {
        setStage("delayed");
      }
    };

    pollForConfirmation();

    return () => {
      cancelled = true;
    };
    // Polling intentionally restarts only when the user taps Refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollRun]);

  const handleRefresh = () => {
    setPollRun((value) => value + 1);
  };

  const handleContinue = async () => {
    setContinuing(true);
    try {
      if (refreshUser) {
        await refreshUser();
      }
    } catch (_) {
      // The entitlement was already confirmed before the button appeared.
    }
    navigateRef.current(isWeb ? '/download-app' : returnPathRef.current, { replace: true });
  };

  const confirmed = stage === "confirmed";
  const delayed = stage === "delayed";

  const continueBtnLabel = isWeb ? t("premium.downloadApp") : t("premium.goToDashboard");

  const title = confirmed
    ? t("premium.paymentConfirmed")
    : delayed
      ? t("premium.stillConfirming")
      : t("premium.confirmingPayment");

  const message = confirmed
    ? t("premium.subscriptionActive")
    : delayed
      ? t("premium.stillConfirmingHint")
      : t("premium.confirmingPaymentHint");

  return (
    <div className="min-h-screen bg-[#F8F7F4] px-6 py-8 pb-safe flex flex-col items-center justify-center">
      <div className="w-full max-w-[320px] text-center">
        <div className="flex justify-center mb-9">
          <div className="w-[92px] h-[92px] rounded-full bg-emerald-50 flex items-center justify-center shadow-[0_0_0_12px_rgba(16,185,129,0.06)]">
            <div className="w-[68px] h-[68px] rounded-full bg-emerald-600 flex items-center justify-center shadow-[0_12px_28px_rgba(5,150,105,0.24)]">
              {confirmed ? (
                <CheckCircle className="w-10 h-10 text-white" strokeWidth={3} aria-hidden="true" />
              ) : (
                <Loader2 className="w-9 h-9 text-white animate-spin" strokeWidth={2.6} aria-hidden="true" />
              )}
            </div>
          </div>
        </div>

        <h1
          className="text-[22px] leading-7 font-semibold mb-2 text-stone-950"
          style={{ fontFamily: "Manrope-Variable, Manrope, sans-serif" }}
        >
          {title}
        </h1>
        <p
          className="text-[14px] leading-5 text-stone-500 mx-auto max-w-[260px]"
          style={{ fontFamily: "Manrope-Variable, Manrope, sans-serif" }}
        >
          {message}
        </p>

        {confirmed && (
          <button
            type="button"
            onClick={handleContinue}
            disabled={continuing}
            className="mt-8 w-full h-12 rounded-full bg-stone-950 text-white font-medium flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-wait"
          >
            <span>{continuing ? t("premium.activatingShort") : continueBtnLabel}</span>
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        )}

        {delayed && (
          <button
            type="button"
            onClick={handleRefresh}
            className="mt-8 mx-auto h-11 px-5 rounded-full border border-stone-300 text-stone-800 font-medium flex items-center justify-center gap-2 bg-white transition-all active:scale-[0.98]"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            <span>{t("premium.refresh")}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default PremiumSuccess;
